import { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { api } from '@/lib/api.ts';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';

export const ImageUpload = forwardRef(({ value, onChange, label = "Imagen", productId }, ref) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const [localFile, setLocalFile] = useState(null);
  const fileInputRef = useRef(null);

  // ======================================================================
  // AGREGADO: Sincronizar la vista previa cuando el valor externo cambia
  // (Esto hace que el logo aparezca al recargar la página)
  // ======================================================================
  useEffect(() => {
    setPreview(value || '');
  }, [value]);
  // ======================================================================

  // ==============================================================================
  // INICIO DE LA MODIFICACIÓN: Nueva lógica de subida con URL Firmada + COMPRESIÓN
  // ==============================================================================
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe pesar más de 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten archivos de imagen');
      return;
    }

    setUploading(true);

    try {
      // ✅ PASO 0: COMPRIMIR IMAGEN AUTOMÁTICAMENTE
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 0.5,          // Máximo 500 KB
        maxWidthOrHeight: 2000,  // Máximo 2000px
        useWebWorker: true,
        quality: 0.9,            // 90% calidad
        fileType: 'image/jpeg'
      });

      // Crear File object con nombre original
      const finalFile = new File([compressedFile], file.name, { type: compressedFile.type });

      // Para productos nuevos, subir inmediatamente en lugar de mostrar preview local
      if (!productId) {
        try {
          // Usar el nuevo endpoint sin productId para generar URL con temp ID
          const presignResponse = await api.post('/api/merchant/products/generate-upload-url/', {
            fileName: finalFile.name,
            contentType: finalFile.type,
          });

          const { uploadURL, filePath } = presignResponse.data;

          if (!uploadURL || !filePath) {
            throw new Error("El backend no devolvió la URL de subida.");
          }

          // Subir el archivo COMPRIMIDO a R2
          const uploadResponse = await fetch(uploadURL, {
            method: 'PUT',
            body: finalFile,
            headers: {
              'Content-Type': finalFile.type,
            },
          });

          if (!uploadResponse.ok) {
            throw new Error('La subida directa al almacenamiento falló.');
          }

          // La imagen ya está subida, pero no tenemos producto aún
          const tempImageUrl = `https://imagenes.tiendasya.store/${filePath}`;
          setPreview(tempImageUrl);
          setLocalFile(null);
          onChange(tempImageUrl);
          toast.success('Imagen subida temporalmente. Se asociará al producto cuando lo guardes.');
        } catch (error) {
          console.error('Error al subir imagen:', error);
          toast.error('Error al subir la imagen. Revisa la consola.');
        } finally {
          setUploading(false);
        }
        return;
      }

      // --- PASO 1: Pedir el "ticket de subida" (URL firmada) a nuestro backend ---
      const presignResponse = await api.post(`/api/merchant/products/${productId}/generate-upload-url/`, {
        fileName: finalFile.name,
        contentType: finalFile.type,
      });

      const { uploadURL, filePath } = presignResponse.data;

      if (!uploadURL || !filePath) {
        throw new Error("El backend no devolvió la URL de subida.");
      }

      // --- PASO 2: Subir el archivo COMPRIMIDO DIRECTAMENTE a Cloudflare R2 ---
      const uploadResponse = await fetch(uploadURL, {
        method: 'PUT',
        body: finalFile,
        headers: {
          'Content-Type': finalFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('La subida directa al almacenamiento falló.');
      }
      
      // --- PASO 3: Informar a nuestro backend que la subida fue exitosa ---
      const { data } = await api.post(`/api/merchant/products/${productId}/media`, {
        file: filePath 
      });

      const finalImageUrl = data.file;
      setPreview(finalImageUrl);
      onChange(finalImageUrl);
      toast.success('Imagen subida y guardada correctamente');

    } catch (error) {
      console.error('Error en el proceso de subida:', error);
      toast.error('Error al subir la imagen. Revisa la consola.');
    } finally {
      setUploading(false);
    }
  };
  // ==============================================================================
  // FIN DE LA MODIFICACIÓN
  // ==============================================================================

  const uploadLocalFile = async (newProductId) => {
    if (!localFile || !newProductId) return;

    setUploading(true);
    try {
      // Usar el nuevo endpoint sin productId para generar URL con temp ID
      const presignResponse = await api.post('/api/merchant/products/generate-upload-url/', {
        fileName: localFile.name,
        contentType: localFile.type,
      });

      const { uploadURL, filePath } = presignResponse.data;

      if (!uploadURL || !filePath) {
        throw new Error("El backend no devolvió la URL de subida.");
      }

      // Subir el archivo a R2
      const uploadResponse = await fetch(uploadURL, {
        method: 'PUT',
        body: localFile,
        headers: {
          'Content-Type': localFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('La subida directa al almacenamiento falló.');
      }
      
      // Informar al backend que la subida fue exitosa
      const { data } = await api.post(`/api/merchant/products/${newProductId}/media`, {
        file: filePath 
      });

      const finalImageUrl = data.file;
      setPreview(finalImageUrl);
      setLocalFile(null); // Limpiar el archivo local
      onChange(finalImageUrl);
      toast.success('Imagen subida correctamente');

    } catch (error) {
      console.error('Error al subir imagen local:', error);
      toast.error('Error al subir la imagen. Revisa la consola.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview('');
    setLocalFile(null);
    onChange(''); // Informa al formulario que la imagen se ha quitado
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Exponer la función para que el componente padre pueda llamarla
  useImperativeHandle(ref, () => ({
    uploadLocalFile,
  }));

  return (
    <div className="space-y-3">
      <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
        <ImageIcon className="h-4 w-4" />
        {label}
      </label>

      {preview ? (
        <div className="relative group flex justify-center">
          <div className="w-full max-w-md h-48 rounded-xl overflow-hidden border-2 border-slate-200 dark:border-zinc-700 bg-slate-100 dark:bg-zinc-950">
            <img 
              src={preview} 
              alt="Vista previa" 
              className="w-full h-full object-contain"
              onError={() => {
                // Evitamos borrar el preview inmediatamente si falla (a veces es latencia de R2)
                console.warn("La imagen está tardando en cargar o falló");
              }}
            />
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 right-2 p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="relative flex justify-center">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id={`image-upload-${label}`}
          />
          <label
            htmlFor={`image-upload-${label}`}
            className={`flex flex-col items-center justify-center w-full max-w-md h-48 border-2 border-dashed rounded-xl transition-all ${
              uploading 
                ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/20 cursor-wait' 
                : 'border-slate-300 dark:border-zinc-700 bg-slate-50 dark:bg-zinc-950 hover:border-sky-500 hover:bg-sky-50 dark:hover:bg-sky-950/20 cursor-pointer'
            }`}
          >
            {uploading ? (
              <>
                <Loader2 className="h-10 w-10 text-blue-500 animate-spin mb-3" />
                <p className="text-sm text-blue-600 dark:text-blue-400 font-semibold">Subiendo imagen...</p>
              </>
            ) : (
              <>
                <Upload className="h-10 w-10 text-slate-400 dark:text-slate-600 mb-3" />
                <p className="text-sm text-slate-600 dark:text-slate-400 font-semibold">
                  Haz clic para seleccionar imagen
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                  JPG, PNG, WEBP o GIF (máx. 5MB)
                </p>
                {!productId && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 font-medium">
                    💡 La imagen se subirá cuando guardes el producto
                  </p>
                )}
              </>
            )}
          </label>
        </div>
      )}

      {!preview && !uploading && (
        <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
          💡 Selecciona una imagen desde tu computador
        </p>
      )}
    </div>
  );
});

ImageUpload.displayName = 'ImageUpload';